import {
    ArrowsRightLeftIcon,
    Bars3Icon,
    ChartBarIcon,
    CheckIcon,
    DocumentChartBarIcon,
    FunnelIcon,
    PaperAirplaneIcon,
    PencilIcon,
    PlusIcon,
    SparklesIcon,
    Squares2X2Icon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { VideoCameraIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStatus } from "../hooks/useAuthStatus";
import {
    fetchAllMatches,
    fetchAllTeams,
    fetchUserPlaylists,
    mergeTeamRecords,
    saveTeam,
} from "../utils/dbutils";
import { uuid } from "../utils/utils";
import { PlaylistViewer, ShareModal } from "./Playlists";

const DEFAULT_NOTES_CATEGORIES = ["Overview", "Serving", "Playing Style", "Mental Game", "Physicality", "Other"];
const SETTINGS_KEY = "psts_notes_categories";
const SELECTED_TEAM_KEY = "psts_teams_selected_guid";

function loadNotesCategories() {
  return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "null") ?? DEFAULT_NOTES_CATEGORIES;
}

const MIN_PANEL = 160;
const MAX_PANEL = 480;
const DEFAULT_PANEL = 240;

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-1 flex-wrap">
      <span className="text-xs text-base-content/50 shrink-0">{label}:</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

function teamInitials(team) {
  if (!team) return "?";
  const name = team.name ?? "";
  if (name.trim()) {
    return name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  }
  const p1 = `${team.player1?.firstName?.[0] ?? ""}${team.player1?.lastName?.[0] ?? ""}`.toUpperCase();
  const p2 = `${team.player2?.firstName?.[0] ?? ""}${team.player2?.lastName?.[0] ?? ""}`.toUpperCase();
  return (p1 + p2).slice(0, 2) || "?";
}

function playerFullName(p) {
  if (!p) return "—";
  return `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "—";
}

function Teams() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  // Left panel resize
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_PANEL);

  // Info panel resize (upper-right split)
  const [infoPanelWidth, setInfoPanelWidth] = useState(320);
  const infoDragging = useRef(false);
  const infoStartX = useRef(0);
  const infoStartWidth = useRef(320);

  // Data
  const [teams, setTeams] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection & search
  const [selectedGuid, setSelectedGuid] = useState(
    () => localStorage.getItem(SELECTED_TEAM_KEY) || null
  );
  const [search, setSearch] = useState("");

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [merging, setMerging] = useState(false);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [sharePlaylistTarget, setSharePlaylistTarget] = useState(null);
  const [viewingPlaylist, setViewingPlaylist] = useState(null);

  // Mobile: show the team list or the details one at a time.
  const [isMobile, setIsMobile] = useState(false);
  const [showList, setShowList] = useState(true);
  // Mobile portrait: stack team details, matches and playlist vertically.
  const [portraitStack, setPortraitStack] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const update = () => setPortraitStack(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Notes
  const [categoryNotes, setCategoryNotes] = useState({});
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState({});
  const [notesCategories] = useState(loadNotesCategories);

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    Promise.all([fetchAllTeams(uid), fetchAllMatches(uid)]).then(([ts, ms]) => {
      const sorted = (ts ?? []).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
      setTeams(sorted);
      setAllMatches(ms ?? []);
      setLoading(false);
      setSelectedGuid((prev) => prev && sorted.some((t) => t.guid === prev) ? prev : null);
    });
  }, [uid]);

  useEffect(() => {
    if (selectedGuid) localStorage.setItem(SELECTED_TEAM_KEY, selectedGuid);
    else localStorage.removeItem(SELECTED_TEAM_KEY);
  }, [selectedGuid]);

  useEffect(() => {
    if (!uid) return;
    fetchUserPlaylists(uid).then(setAllPlaylists);
  }, [uid]);

  // ── Drag handles ─────────────────────────────────────────────────────────────
  const onMouseDown = useCallback(
    (e) => {
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = panelWidth;
      e.preventDefault();
    },
    [panelWidth]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (dragging.current) {
        setPanelWidth(Math.min(MAX_PANEL, Math.max(MIN_PANEL, startWidth.current + (e.clientX - startX.current))));
      }
      if (infoDragging.current) {
        setInfoPanelWidth(Math.min(800, Math.max(180, infoStartWidth.current + (e.clientX - infoStartX.current))));
      }
    };
    const onUp = () => { dragging.current = false; infoDragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const selectedTeam = teams.find((t) => t.guid === selectedGuid) ?? null;

  useEffect(() => {
    const raw = selectedTeam?.notes ?? null;
    let parsed = {};
    if (raw && typeof raw === "object") parsed = raw;
    else if (typeof raw === "string" && raw.trim()) parsed = { Overview: [{ text: raw, ts: Date.now() }] };
    setCategoryNotes(parsed);
    setNotesEditing(false);
    setNotesDraft({});
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuid]);

  const filteredTeams = teams.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.name ?? "").toLowerCase().includes(s) ||
      playerFullName(t.player1).toLowerCase().includes(s) ||
      playerFullName(t.player2).toLowerCase().includes(s)
    );
  });

  // Compute the effective guid of a team object (explicit guid or derived from player guids)
  const teamGuid = (team) =>
    team?.guid ||
    (team?.player1?.guid && team?.player2?.guid
      ? `${team.player1.guid}_${team.player2.guid}`
      : null);

  // Match count per team guid
  const matchCountByGuid = {};
  for (const m of allMatches) {
    if (!m.isDoubles) continue;
    const gA = teamGuid(m.teamA);
    const gB = teamGuid(m.teamB);
    if (gA) matchCountByGuid[gA] = (matchCountByGuid[gA] ?? 0) + 1;
    if (gB) matchCountByGuid[gB] = (matchCountByGuid[gB] ?? 0) + 1;
  }

  // Duplicate name detection
  const nameCounts = {};
  for (const t of teams) {
    const name = (t.name ?? "").trim().toLowerCase();
    if (name) nameCounts[name] = (nameCounts[name] ?? 0) + 1;
  }
  const duplicateNames = new Set(Object.keys(nameCounts).filter((n) => nameCounts[n] > 1));

  const teamMatches = allMatches
    .filter((m) => m.isDoubles && (teamGuid(m.teamA) === selectedGuid || teamGuid(m.teamB) === selectedGuid))
    .sort((a, b) => (b.matchDate ?? 0) - (a.matchDate ?? 0));

  const teamPlaylists = allPlaylists.filter(
    (p) => p.playerGuid === selectedGuid || p.data?.playerGuid === selectedGuid
  );

  // ── Edit handlers ─────────────────────────────────────────────────────────────
  const startEditing = () => {
    if (!selectedTeam) return;
    setEditFields({
      name: selectedTeam.name ?? "",
      preferredFormation: selectedTeam.preferredFormation ?? "Standard Formation",
      player1CourtSide: selectedTeam.player1CourtSide ?? "",
      player2CourtSide: selectedTeam.player2CourtSide ?? "",
    });
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const saveEditing = async () => {
    if (!selectedTeam) return;
    setSaving(true);
    const updated = {
      ...selectedTeam,
      name: editFields.name,
      preferredFormation: editFields.preferredFormation ?? "Standard Formation",
      player1CourtSide: editFields.player1CourtSide || null,
      player2CourtSide: editFields.player2CourtSide || null,
      notes: categoryNotes,
    };
    await saveTeam(updated);
    setTeams((prev) =>
      prev.map((t) => (t.guid === updated.guid ? updated : t))
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
    );
    setSaving(false);
    setEditing(false);
  };

  const setField = (k, v) => setEditFields((prev) => ({ ...prev, [k]: v }));

  const saveCategoryNotes = async (updated) => {
    if (!selectedTeam) return;
    const updatedTeam = { ...selectedTeam, notes: updated };
    await saveTeam(updatedTeam);
    setTeams((prev) => prev.map((t) => (t.guid === updatedTeam.guid ? updatedTeam : t)));
  };

  const enterNotesEdit = () => {
    const draft = {};
    notesCategories.forEach((cat) => {
      draft[cat] = (categoryNotes[cat] ?? []).map((c) => c.text).join("\n");
    });
    setNotesDraft(draft);
    setNotesEditing(true);
  };

  const saveNotesEdit = async () => {
    const updated = {};
    notesCategories.forEach((cat) => {
      const lines = (notesDraft[cat] ?? "").split("\n").map((s) => s.trim()).filter(Boolean);
      const existing = categoryNotes[cat] ?? [];
      updated[cat] = lines.map((text) => existing.find((c) => c.text === text) ?? { text, ts: Date.now() });
    });
    setCategoryNotes(updated);
    setNotesEditing(false);
    setNotesDraft({});
    await saveCategoryNotes(updated);
  };

  const cancelNotesEdit = () => { setNotesEditing(false); setNotesDraft({}); };

  const handleMergeInto = async (targetGuid) => {
    if (!selectedGuid || !targetGuid) return;
    setMerging(true);
    const result = await mergeTeamRecords(uid, selectedGuid, targetGuid);
    setMerging(false);
    if (result.error) { toast.error("Merge failed: " + result.error); return; }
    const target = teams.find((t) => t.guid === targetGuid);
    toast.success(`Merged into ${target?.name ?? "team"} (${result.updatedCount} match(es) updated)`);
    setShowMergeModal(false);
    setLoading(true);
    const [ts, ms] = await Promise.all([fetchAllTeams(uid), fetchAllMatches(uid)]);
    setTeams((ts ?? []).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));
    setAllMatches(ms ?? []);
    setLoading(false);
    setSelectedGuid(targetGuid);
  };

  const createNewTeam = async (fields) => {
    const guid = uuid();
    const team = { uid, guid, name: fields.name.trim(), player1: null, player2: null, notes: {} };
    await saveTeam(team);
    setTeams((prev) => [...prev, team].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));
    setSelectedGuid(guid);
    setShowNewTeamModal(false);
  };

  // Match selection for analysis
  const [selectedMatchGuids, setSelectedMatchGuids] = useState(new Set());
  useEffect(() => { setSelectedMatchGuids(new Set()); }, [selectedGuid]);

  const toggleMatchSelect = (guid) => {
    setSelectedMatchGuids((prev) => { const next = new Set(prev); if (next.has(guid)) next.delete(guid); else next.add(guid); return next; });
  };
  const toggleSelectAllMatches = () => {
    if (selectedMatchGuids.size === teamMatches.length) setSelectedMatchGuids(new Set());
    else setSelectedMatchGuids(new Set(teamMatches.map((m) => m.guid)));
  };

  const opponentName = (m) => {
    const isA = teamGuid(m.teamA) === selectedGuid;
    return (isA ? m.teamB?.name : m.teamA?.name) ?? "?";
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (viewingPlaylist) {
    return <PlaylistViewer playlist={viewingPlaylist} onBack={() => setViewingPlaylist(null)} />;
  }

  return (
    <div className="flex h-[calc(100vh-3rem)] overflow-hidden">

      {/* ── Left panel: team list ──────────────────────────────────────── */}
      {(!isMobile || showList) && (
      <div className="flex flex-col shrink-0 border-r border-base-300" style={{ width: isMobile ? "100%" : panelWidth }}>
        <div className="px-2 py-2 border-b border-base-300 bg-base-200 shrink-0 flex gap-1">
          <input
            type="text"
            className="input input-bordered input-sm flex-1 min-w-0"
            placeholder="Search teams…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn btn-ghost btn-sm btn-square shrink-0"
            title="New team"
            onClick={() => setShowNewTeamModal(true)}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <span className="loading loading-spinner loading-md" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1">
            {filteredTeams.length === 0 && (
              <p className="text-xs text-base-content/50 px-3 py-4 text-center">
                {teams.length === 0 ? "No teams yet." : "No matches."}
              </p>
            )}
            {filteredTeams.map((t) => {
              const matchCount = matchCountByGuid[t.guid] ?? 0;
              const isDuplicate = duplicateNames.has((t.name ?? "").trim().toLowerCase());
              return (
                <button
                  key={t.guid}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-base-300/40 flex items-center gap-2 hover:bg-base-200${
                    selectedGuid === t.guid ? " bg-primary/10 font-semibold" : ""
                  }`}
                  onClick={() => { setSelectedGuid(t.guid); if (isMobile) setShowList(false); }}
                >
                  <div className="w-6 h-6 rounded-full bg-base-300 flex items-center justify-center shrink-0 text-xs font-bold text-base-content/50">
                    {teamInitials(t)}
                  </div>
                  <span className="truncate flex-1">{t.name || "Unnamed Team"}</span>
                  <span className="shrink-0 flex items-center gap-1">
                    {isDuplicate && (
                      <span className="badge badge-warning badge-xs" title="Duplicate name — consider merging">!</span>
                    )}
                    <span className="text-xs text-base-content/40">{matchCount}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Drag handle */}
      {!isMobile && (
      <div
        className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
        onMouseDown={onMouseDown}
      />
      )}

      {/* ── Right panel ──────────────────────────────────────────────────── */}
      {(!isMobile || !showList) && (
      <div className={`flex flex-col flex-1 ${portraitStack ? "overflow-y-auto" : "overflow-hidden"}`}>
        {isMobile && (
          <button
            className="btn btn-ghost btn-sm gap-1 self-start m-1 shrink-0"
            onClick={() => setShowList(true)}
            title="Back to teams list"
          >
            <Bars3Icon className="w-4 h-4" />
            Teams
          </button>
        )}
        {!selectedTeam ? (
          <div className="flex items-center justify-center flex-1 text-base-content/40 text-sm">
            Select a team from the list
          </div>
        ) : (
          <>
            {/* ── Upper panel (Team details) ── */}
            <div
              className={`shrink-0 border-b border-base-300 ${portraitStack ? "flex flex-col" : "flex overflow-hidden"}`}
              style={portraitStack ? undefined : { height: "42%", minHeight: "200px", maxHeight: "380px" }}
            >
              {/* Team info (left half — resizable) */}
              <div
                className={`p-4 shrink-0 ${portraitStack ? "w-full" : "overflow-y-auto"}`}
                style={portraitStack ? undefined : { width: infoPanelWidth }}
              >
                {/* Header row: avatar · name+players · action buttons */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="shrink-0">
                    <div className="w-16 h-16 rounded-box bg-base-300 flex items-center justify-center text-2xl font-bold text-base-content/30">
                      {teamInitials(selectedTeam)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <>
                        <input
                          className="input input-bordered input-sm w-full mb-1"
                          value={editFields.name}
                          placeholder="Team name"
                          onChange={(e) => setField("name", e.target.value)}
                        />
                        <select
                          className="select select-bordered select-xs w-full mt-1"
                          value={editFields.preferredFormation ?? "Standard Formation"}
                          onChange={(e) => setField("preferredFormation", e.target.value)}
                        >
                          <option>Standard Formation</option>
                          <option>Australian Formation</option>
                          <option>I Formation</option>
                          <option>2-Back Formation</option>
                        </select>
                      </>
                    ) : (
                      <div className="text-base font-bold leading-tight">
                        {(selectedTeam.name || "Unnamed Team")
                          .split(/(?<=\S)[/ ]+(?=\S)/)
                          .map((part, i) => (
                            <div key={i}>{part}</div>
                          ))}
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5 mt-1">
                      {editing ? (
                        <>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-base-content/60 truncate flex-1">
                              {playerFullName(selectedTeam.player1)}
                            </span>
                            <select
                              className="select select-bordered select-xs shrink-0"
                              value={editFields.player1CourtSide ?? ""}
                              onChange={(e) => setField("player1CourtSide", e.target.value)}
                            >
                              <option value="">Auto</option>
                              <option value="deuce">Deuce</option>
                              <option value="ad">Ad</option>
                              <option value="balanced">Balanced</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-base-content/60 truncate flex-1">
                              {playerFullName(selectedTeam.player2)}
                            </span>
                            <select
                              className="select select-bordered select-xs shrink-0"
                              value={editFields.player2CourtSide ?? ""}
                              onChange={(e) => setField("player2CourtSide", e.target.value)}
                            >
                              <option value="">Auto</option>
                              <option value="deuce">Deuce</option>
                              <option value="ad">Ad</option>
                              <option value="balanced">Balanced</option>
                            </select>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-base-content/60 truncate">
                            {playerFullName(selectedTeam.player1)}
                            {selectedTeam.player1CourtSide && (
                              <span className="ml-1 badge badge-xs badge-ghost">{selectedTeam.player1CourtSide}</span>
                            )}
                          </span>
                          <span className="text-xs text-base-content/60 truncate">
                            {playerFullName(selectedTeam.player2)}
                            {selectedTeam.player2CourtSide && (
                              <span className="ml-1 badge badge-xs badge-ghost">{selectedTeam.player2CourtSide}</span>
                            )}
                          </span>
                          <div className="mt-1">
                            <InfoRow label="Formation" value={selectedTeam.preferredFormation ?? "Standard Formation"} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 shrink-0">
                    {editing ? (
                      <>
                        <button className="btn btn-xs btn-success" onClick={saveEditing} disabled={saving}>
                          {saving ? <span className="loading loading-spinner loading-xs" /> : <CheckIcon className="w-3.5 h-3.5" />}
                        </button>
                        <button className="btn btn-xs btn-ghost" onClick={cancelEditing}>
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-xs btn-ghost" onClick={startEditing} title="Edit team name">
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="btn btn-xs btn-ghost text-warning"
                          onClick={() => setShowMergeModal(true)}
                          title="Merge this team into another"
                        >
                          <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Info/Notes drag handle */}
              {!portraitStack && (
              <div
                className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
                onMouseDown={(e) => {
                  infoDragging.current = true;
                  infoStartX.current = e.clientX;
                  infoStartWidth.current = infoPanelWidth;
                  e.preventDefault();
                }}
              />
              )}

              {/* Notes panel */}
              <div
                className={`flex flex-col ${portraitStack ? "w-full border-t border-base-300" : "flex-1 overflow-hidden"}`}
                style={portraitStack ? undefined : { minWidth: "180px" }}
              >
                <div className="flex items-center gap-1 px-3 py-2 border-b border-base-300 shrink-0">
                  <span className="text-xs font-semibold uppercase tracking-wide text-base-content/50 flex-1">Notes</span>
                  {!notesEditing && selectedTeam && (
                    <button className="btn btn-ghost btn-xs" onClick={enterNotesEdit} title="Edit notes">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {notesEditing && (
                    <>
                      <button className="btn btn-ghost btn-xs text-success" onClick={saveNotesEdit} title="Save">
                        <CheckIcon className="w-3.5 h-3.5" />
                      </button>
                      <button className="btn btn-ghost btn-xs text-error" onClick={cancelNotesEdit} title="Cancel">
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
                <div className={`px-3 py-3 flex flex-col gap-4 ${portraitStack ? "" : "overflow-y-auto flex-1"}`}>
                  {notesCategories.map((cat) => (
                    <div key={cat}>
                      <div className="text-xs font-bold uppercase tracking-wide text-primary mb-1">{cat}</div>
                      {notesEditing ? (
                        <textarea
                          className="textarea textarea-bordered textarea-xs w-full resize-y text-sm leading-relaxed"
                          rows={3}
                          placeholder="One note per line…"
                          value={notesDraft[cat] ?? ""}
                          onChange={(e) => setNotesDraft((prev) => ({ ...prev, [cat]: e.target.value }))}
                        />
                      ) : (
                        <div className="flex flex-col gap-0.5 pl-0.5">
                          {(categoryNotes[cat] ?? []).length === 0 ? (
                            <span className="text-xs text-base-content/30 italic">—</span>
                          ) : (
                            (categoryNotes[cat] ?? []).map((c, i) => (
                              <p key={i} className="text-xs leading-relaxed text-base-content">{c.text}</p>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Lower panel ── */}
            <div className={portraitStack ? "flex flex-col shrink-0" : "flex flex-1 overflow-hidden"}>
              {/* Matches */}
              <div className={`flex flex-col ${portraitStack ? "w-full border-b border-base-300" : "flex-1 overflow-hidden border-r border-base-300"}`}>
                <div className="px-3 py-2 border-b border-base-300 bg-base-200 shrink-0 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    Matches ({teamMatches.length}){selectedMatchGuids.size > 0 && (
                      <span className="ml-2 badge badge-primary badge-sm">{selectedMatchGuids.size} selected</span>
                    )}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`Match stats (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/stats", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <ChartBarIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`General report (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/report", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <DocumentChartBarIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`Filters analysis (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/filters-analysis", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <FunnelIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`Shots analysis (${selectedMatchGuids.size})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/shots-analysis", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <Squares2X2Icon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      title={`AI Report (${selectedMatchGuids.size} match${selectedMatchGuids.size !== 1 ? "es" : ""})`}
                      disabled={selectedMatchGuids.size === 0}
                      onClick={() => navigate("/ai-report", { state: { matchGuids: [...selectedMatchGuids], playerGuid: selectedGuid } })}
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className={portraitStack ? "" : "overflow-y-auto flex-1"}>
                  {teamMatches.length === 0 ? (
                    <p className="text-xs text-base-content/50 px-4 py-6 text-center">
                      No doubles matches found for this team.
                    </p>
                  ) : (
                    <table className="table table-xs w-full">
                      <thead>
                        <tr>
                          <th className="w-6">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs"
                              checked={teamMatches.length > 0 && selectedMatchGuids.size === teamMatches.length}
                              onChange={toggleSelectAllMatches}
                            />
                          </th>
                          <th>Date</th>
                          <th>Opponent</th>
                          <th>Tournament</th>
                          <th className="w-10 text-center">Video</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMatches.map((m) => (
                          <tr
                            key={m.guid ?? m.firestoreId}
                            className={`hover${selectedMatchGuids.has(m.guid) ? " bg-base-200" : ""}`}
                          >
                            <td>
                              <input
                                type="checkbox"
                                className="checkbox checkbox-xs"
                                checked={selectedMatchGuids.has(m.guid)}
                                onChange={() => toggleMatchSelect(m.guid)}
                              />
                            </td>
                            <td className="whitespace-nowrap">
                              {m.matchDate ? new Date(m.matchDate).toLocaleDateString() : "—"}
                            </td>
                            <td>{opponentName(m)}</td>
                            <td className="max-w-[140px] truncate">{m.tournament?.name || "—"}</td>
                            <td className="text-center w-10">
                              <button
                                className="btn btn-ghost btn-xs btn-square"
                                title="Video analysis"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/video-analysis", { state: { matchGuids: [m.guid], playerGuid: selectedGuid } });
                                }}
                              >
                                <VideoCameraIcon className={`w-3.5 h-3.5 ${m.videoFile ? "text-success" : ""}`} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Playlists */}
              <div
                className={`flex flex-col ${portraitStack ? "w-full border-t border-base-300" : "overflow-hidden shrink-0"}`}
                style={portraitStack ? undefined : { width: "32%", minWidth: "180px" }}
              >
                <div className="px-3 py-2 border-b border-base-300 bg-base-200 shrink-0 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                  Playlists ({teamPlaylists.length})
                </div>
                <div className={portraitStack ? "" : "overflow-y-auto flex-1"}>
                  {teamPlaylists.length === 0 ? (
                    <p className="text-xs text-base-content/50 px-4 py-6 text-center">
                      No playlists for this team.
                    </p>
                  ) : (
                    teamPlaylists.map((pl) => (
                      <div
                        key={pl.firestoreId}
                        className="flex items-center gap-1 px-2 py-2 border-b border-base-300/40 hover:bg-base-200"
                      >
                        <button className="flex-1 text-left min-w-0" onClick={() => setViewingPlaylist(pl)}>
                          <div className="text-sm font-medium truncate">{pl.name}</div>
                          <div className="text-xs text-base-content/50 mt-0.5">
                            {pl.updatedAt ? new Date(pl.updatedAt).toLocaleDateString() : ""}
                            {pl.items != null ? ` · ${pl.items} clips` : ""}
                          </div>
                        </button>
                        <button
                          className="btn btn-ghost btn-xs btn-square tooltip tooltip-left"
                          data-tip="Share"
                          disabled={!uid}
                          onClick={(e) => { e.stopPropagation(); setSharePlaylistTarget(pl); }}
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="btn btn-ghost btn-xs btn-square tooltip tooltip-left"
                          data-tip="Open playlist"
                          onClick={(e) => { e.stopPropagation(); setViewingPlaylist(pl); }}
                        >
                          <VideoCameraIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {showMergeModal && selectedTeam && (
        <MergeIntoModal
          fromTeam={selectedTeam}
          teams={teams.filter((t) => t.guid !== selectedGuid)}
          merging={merging}
          onClose={() => setShowMergeModal(false)}
          onConfirm={handleMergeInto}
        />
      )}

      {sharePlaylistTarget && (
        <ShareModal
          playlists={[sharePlaylistTarget]}
          senderUid={uid}
          senderEmail={firebaseUser?.email ?? ""}
          senderName={firebaseUser?.displayName ?? firebaseUser?.email ?? ""}
          onClose={() => setSharePlaylistTarget(null)}
        />
      )}

      {showNewTeamModal && (
        <NewTeamModal onClose={() => setShowNewTeamModal(false)} onCreate={createNewTeam} />
      )}
    </div>
  );
}

// ── NewTeamModal ──────────────────────────────────────────────────────────────

function NewTeamModal({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate({ name });
    setSaving(false);
  };

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box w-full max-w-sm flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">New Team</h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={saving}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-base-content/50">Team Name</span>
          <input
            className="input input-bordered input-sm w-full"
            value={name}
            placeholder="Team name"
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
        </div>
        <div className="modal-action mt-0">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={!name.trim() || saving}
            onClick={handleCreate}
          >
            {saving ? <span className="loading loading-spinner loading-xs" /> : "Create"}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !saving && onClose()} />
    </div>
  );
}

// ── MergeIntoModal ────────────────────────────────────────────────────────────

function MergeIntoModal({ fromTeam, teams, merging, onClose, onConfirm }) {
  const [search, setSearch] = useState("");
  const [targetGuid, setTargetGuid] = useState(null);

  const filtered = teams.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.name ?? "").toLowerCase().includes(s) ||
      playerFullName(t.player1).toLowerCase().includes(s) ||
      playerFullName(t.player2).toLowerCase().includes(s)
    );
  });

  const target = teams.find((t) => t.guid === targetGuid) ?? null;
  const fromName = fromTeam.name || "Unnamed Team";
  const toName = target?.name || "Unnamed Team";

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box w-full max-w-sm flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">
            Merge <span className="text-primary">{fromName}</span> into…
          </h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={merging}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-base-content/60">
          All doubles matches for <strong>{fromName}</strong> will be reassigned to the selected team.{" "}
          <strong>{fromName}</strong> will then be deleted.
        </p>

        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Search teams…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <div className="border border-base-300 rounded-box overflow-y-auto max-h-52 flex flex-col">
          {filtered.length === 0 ? (
            <p className="text-xs text-base-content/40 px-3 py-4 text-center">No teams found.</p>
          ) : (
            filtered.map((t) => {
              const isSelected = t.guid === targetGuid;
              return (
                <button
                  key={t.guid}
                  className={`text-left px-3 py-2 text-sm border-b border-base-300/40 last:border-0 flex items-center gap-2 hover:bg-base-200 transition-colors${isSelected ? " bg-primary/10 font-semibold" : ""}`}
                  onClick={() => setTargetGuid(t.guid)}
                >
                  <div className="w-5 h-5 rounded-full bg-base-300 flex items-center justify-center shrink-0 text-xs font-bold text-base-content/50">
                    {teamInitials(t)}
                  </div>
                  <span className="truncate flex-1">{t.name || "Unnamed Team"}</span>
                  {isSelected && <CheckIcon className="w-4 h-4 text-primary ml-auto shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {targetGuid && (
          <div className="alert alert-warning py-2 text-xs">
            <span>
              <strong>{fromName}</strong> will be deleted. All their matches will be assigned to{" "}
              <strong>{toName}</strong>.
            </span>
          </div>
        )}

        <div className="modal-action mt-0">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={merging}>Cancel</button>
          <button
            className="btn btn-warning btn-sm"
            disabled={!targetGuid || merging}
            onClick={() => onConfirm(targetGuid)}
          >
            {merging ? <span className="loading loading-spinner loading-xs" /> : "Merge"}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !merging && onClose()} />
    </div>
  );
}

export default Teams;
